/**
 * Calendar toolbar with navigation, search, and view controls
 */

import {
	AlertCircle,
	Calendar as CalendarIcon,
	ChevronLeft,
	ChevronRight,
	Eye,
	EyeOff,
	Search,
	Settings as SettingsIcon,
	Stethoscope,
	UserX,
	X,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DatePickerDialog } from '@/components/dialogs/DatePickerDialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	formatMonthYear,
	getNextWeek,
	getPreviousWeek,
	getToday,
	getWeekNumber,
} from '@/lib/calendar-utils'
import { VIEW_MODES } from '@/lib/constants'
import type { CalendarView } from '@/types/calendar'
import { cn } from '@/utils/misc'

interface CalendarToolbarProps {
	currentDate: Date
	view: CalendarView
	onNavigate: (date: Date) => void
	onViewChange: (view: CalendarView) => void
	onSearch: (query: string) => void
	onSearchHere: (query: string) => void
	onSearchAll: (query: string) => void
	onOpenCancelled: () => void
	onOpenNoShow: () => void
	searchQuery: string
	calendarType: 'ZUB' | 'DH' | 'OTHER'
	ghostType: 'ZUB' | 'DH' | 'OTHER' | null
	showGhostEvents: boolean
	onToggleGhostEvents: () => void
	/** Acute-case drawer — ZUB only; omit the handler to hide the button. */
	onOpenAcuteDesk?: () => void
	acuteUnresolvedCount?: number
	/** True when the count could not be fetched — must not render as zero. */
	acuteCountStale?: boolean
}

export function CalendarToolbar({
	currentDate,
	view,
	onNavigate,
	onViewChange,
	onSearch,
	onSearchHere,
	onSearchAll,
	onOpenCancelled,
	onOpenNoShow,
	searchQuery,
	calendarType: _calendarType,
	ghostType,
	showGhostEvents,
	onToggleGhostEvents,
	onOpenAcuteDesk,
	acuteUnresolvedCount = 0,
	acuteCountStale = false,
}: CalendarToolbarProps) {
	const [localSearch, setLocalSearch] = useState(searchQuery)
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
	const navigate = useNavigate()

	// Sync localSearch with parent searchQuery (e.g., when Escape is pressed)
	useEffect(() => {
		setLocalSearch(searchQuery)
	}, [searchQuery])

	const handleDatePickerSelect = useCallback(
		(date: Date) => {
			onNavigate(date)
		},
		[onNavigate],
	)

	const handlePrevious = useCallback(() => {
		if (view === 'day') {
			const prev = new Date(currentDate)
			prev.setDate(prev.getDate() - 1)
			onNavigate(prev)
		} else {
			onNavigate(getPreviousWeek(currentDate))
		}
	}, [currentDate, view, onNavigate])

	const handleNext = useCallback(() => {
		if (view === 'day') {
			const next = new Date(currentDate)
			next.setDate(next.getDate() + 1)
			onNavigate(next)
		} else {
			onNavigate(getNextWeek(currentDate))
		}
	}, [currentDate, view, onNavigate])

	const handleToday = useCallback(() => {
		onNavigate(getToday())
	}, [onNavigate])

	const handleSearchChange = useCallback((value: string) => {
		setLocalSearch(value)
		// Don't trigger search on typing - only on button click
	}, [])

	const handleClearSearch = useCallback(() => {
		setLocalSearch('')
		onSearch('') // This will clear both searchQuery and activeSearchQuery in parent
	}, [onSearch])

	const weekNumber = getWeekNumber(currentDate)

	return (
		<div className="mb-4 flex flex-wrap items-center justify-between gap-4 rounded-xl bg-white p-4 shadow-sm border border-[#e8e4df]">
			{/* Left section: Navigation */}
			<div className="flex items-center gap-3">
				<button
					type="button"
					onClick={handleToday}
					className="inline-flex items-center h-9 px-4 rounded-lg text-sm font-medium bg-[#d7ad6c] text-white hover:bg-[#c49b5a] transition-colors shadow-sm"
				>
					<CalendarIcon className="mr-2 h-4 w-4" />
					Dnes
				</button>

				<div className="flex items-center">
					<button
						type="button"
						onClick={handlePrevious}
						title={view === 'day' ? 'Předchozí den' : 'Předchozí týden'}
						className="inline-flex items-center justify-center h-10 w-10 rounded-l-lg border border-[#ddd5c8] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-colors"
					>
						<ChevronLeft className="h-4 w-4" />
					</button>
					<button
						type="button"
						onClick={handleNext}
						title={view === 'day' ? 'Další den' : 'Další týden'}
						className="inline-flex items-center justify-center h-10 w-10 rounded-r-lg border border-l-0 border-[#ddd5c8] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-colors"
					>
						<ChevronRight className="h-4 w-4" />
					</button>
				</div>

				<button
					type="button"
					onClick={() => setIsDatePickerOpen(true)}
					className="ml-2 text-left hover:bg-[#f5ebe0] rounded-lg px-2 py-1 -mx-2 -my-1 transition-colors"
					title="Kliknutím vyberte měsíc a rok"
				>
					<h2
						className="font-semibold capitalize text-[#3d3428]"
						style={{ fontSize: 'var(--font-size-toolbar-title)' }}
					>
						{formatMonthYear(currentDate)}
					</h2>
					<p
						className="font-medium text-[#6b5c4c]"
						style={{ fontSize: 'calc(var(--font-size-toolbar-title) * 0.8)' }}
					>
						{weekNumber}. týden
					</p>
				</button>
			</div>

			{/* Center section: Search */}
			<div className="flex items-center gap-2 flex-1 max-w-xl">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#a09689]" />
					<input
						type="text"
						placeholder="Vyhledat termín..."
						value={localSearch}
						onChange={(e) => handleSearchChange(e.target.value)}
						className="flex h-10 w-full rounded-lg border border-[#ddd5c8] bg-white px-3 py-2 pl-9 pr-9 text-sm text-[#3d3428] placeholder:text-[#a09689] focus:outline-none focus:border-[#d7ad6c] focus:ring-2 focus:ring-[#d7ad6c]/20 transition-colors"
					/>
					{localSearch && (
						<button
							type="button"
							onClick={handleClearSearch}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a09689] hover:text-[#5c5142] transition-colors"
						>
							<X className="h-4 w-4" />
						</button>
					)}
				</div>

				{/* Search action buttons */}
				<button
					type="button"
					onClick={() => onSearchHere(localSearch)}
					disabled={!localSearch}
					className="h-10 px-4 rounded-lg text-sm font-medium border border-[#ddd5c8] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					Zde
				</button>
				<button
					type="button"
					onClick={() => onSearchAll(localSearch)}
					disabled={!localSearch}
					className="h-10 px-4 rounded-lg text-sm font-medium border border-[#ddd5c8] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					Dohromady
				</button>
			</div>

			{/* Right section: View controls and actions */}
			<div className="flex items-center gap-3">
				<Select
					value={view}
					onValueChange={(value) => onViewChange(value as CalendarView)}
				>
					<SelectTrigger className="w-[160px] border-[#ddd5c8] bg-white text-[#3d3428] hover:border-[#d7ad6c] focus:border-[#d7ad6c] focus:ring-2 focus:ring-[#d7ad6c]/20 [&>svg]:text-[#a09689]">
						<SelectValue />
					</SelectTrigger>
					<SelectContent className="border-[#ddd5c8] bg-white">
						{Object.entries(VIEW_MODES).map(([key, label]) => (
							<SelectItem
								key={key}
								value={key}
								className="text-[#3d3428] focus:bg-[#f5ebe0]"
							>
								{label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				{ghostType && (
					<button
						type="button"
						onClick={onToggleGhostEvents}
						title={
							showGhostEvents
								? `Skrýt termíny z kalendáře ${ghostType}`
								: `Zobrazit termíny z kalendáře ${ghostType} jako náhled`
						}
						aria-pressed={showGhostEvents}
						className={cn(
							'inline-flex items-center h-10 px-4 rounded-lg text-sm font-medium border transition-colors',
							showGhostEvents
								? 'border-[#d7ad6c] bg-[#f5ebe0] text-[#5c5142]'
								: 'border-[#ddd5c8] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c]',
						)}
					>
						{showGhostEvents ? (
							<Eye className="mr-2 h-4 w-4 text-[#d7ad6c]" />
						) : (
							<EyeOff className="mr-2 h-4 w-4 text-[#a09689]" />
						)}
						{ghostType}
					</button>
				)}

				<button
					type="button"
					onClick={onOpenCancelled}
					className="inline-flex items-center h-10 px-4 rounded-lg text-sm font-medium border border-[#ddd5c8] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-colors"
				>
					<AlertCircle className="mr-2 h-4 w-4 text-[#a09689]" />
					Zrušené
				</button>

				<button
					type="button"
					onClick={onOpenNoShow}
					className="inline-flex items-center h-10 px-4 rounded-lg text-sm font-medium border border-[#ddd5c8] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-colors"
				>
					<UserX className="mr-2 h-4 w-4 text-[#a09689]" />
					Nepřišli
				</button>

				{onOpenAcuteDesk && (
					<button
						type="button"
						onClick={onOpenAcuteDesk}
						title="Akutní případy od telefonního asistenta"
						className="relative inline-flex items-center h-10 px-4 rounded-lg text-sm font-medium border border-[#ddd5c8] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-colors"
					>
						<Stethoscope className="mr-2 h-4 w-4 text-[#a09689]" />
						Akutní
						{/* A count we failed to fetch shows "!" rather than nothing —
						    rendering an unknown count as zero would tell the surgery
						    there is nothing to do when there may be urgent cases. */}
						{acuteCountStale ? (
							<span
								title="Počet akutních případů se nepodařilo načíst — nemusí být aktuální"
								className="absolute -right-1.5 -top-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[11px] font-semibold text-white"
							>
								!
							</span>
						) : (
							acuteUnresolvedCount > 0 && (
								<span className="absolute -right-1.5 -top-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[11px] font-semibold text-white">
									{acuteUnresolvedCount}
								</span>
							)
						)}
					</button>
				)}

				<button
					type="button"
					onClick={() => navigate('/settings')}
					title="Nastavení"
					className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-[#ddd5c8] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-colors"
				>
					<SettingsIcon className="h-4 w-4 text-[#a09689]" />
				</button>
			</div>

			{/* Date Picker Dialog */}
			<DatePickerDialog
				open={isDatePickerOpen}
				onClose={() => setIsDatePickerOpen(false)}
				currentDate={currentDate}
				onSelect={handleDatePickerSelect}
			/>
		</div>
	)
}
